"use client";

import { Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { parseAsString } from "nuqs";
import { type IQuerySearchParam } from "@/types/query-search-params";
import { useQuerySearchParams } from "@/hooks/use-query-search-params";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/components/ui/sonner";
import { getInvitationTokenInfo, acceptInvitation } from "@/lib/api/generated";
import { refreshOrganizationContext } from "@/lib/api/org-context";
import { useAuth } from "../../hooks/use-auth";
import {
  INVITATION_TOKEN_PARAM,
  invitationEmailsAreDefinitelyDifferent,
} from "@/lib/invitation-links";
import { resetOnboardingCheck } from "@/components/layout/onboarding-guard";
import { resetOrgCache } from "@/hooks/use-org";
import { AuthPageHeader } from "../page-header";
import { InviteOrgCard } from "./org-card";
import { ExistingUserAccept } from "./existing-user";
import { WrongAccount } from "./wrong-account";
import { UnauthenticatedInviteActions } from "./unauthenticated";
import { InviteError } from "./error";
import { InviteSkeleton } from "./skeleton";

const queryParams = {
  INVITATION_TOKEN: {
    field: INVITATION_TOKEN_PARAM,
    parser: parseAsString.withDefault(""),
  },
  TOKEN: {
    field: "token",
    parser: parseAsString.withDefault(""),
  },
} satisfies Record<string, IQuerySearchParam>;

const NOT_PENDING_MESSAGES: Record<string, string> = {
  accepted: "This invitation has already been accepted.",
  expired: "This invitation has expired.",
  revoked: "This invitation has been revoked.",
};

function InviteContent() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [acceptedWorkosOrgId, setAcceptedWorkosOrgId] = useState<string | null>(
    null,
  );

  const [invitationToken] = useQuerySearchParams(queryParams.INVITATION_TOKEN);
  const [tokenParam] = useQuerySearchParams(queryParams.TOKEN);
  const token = invitationToken || tokenParam;

  const completeAcceptance = useMutation({
    mutationFn: async (workosOrgId: string) => {
      if (!workosOrgId) {
        throw new Error(
          "Invitation accepted, but the organization context was missing.",
        );
      }
      await refreshOrganizationContext(workosOrgId);
    },
    onSuccess: async () => {
      resetOnboardingCheck();
      router.push("/projects");
      await resetOrgCache(queryClient, router);
    },
    onError: (error: Error) => {
      showToast.error("Failed to refresh organization context", {
        description: error.message || "Please try again before continuing.",
      });
    },
  });

  const acceptExisting = useMutation({
    mutationFn: () => acceptInvitation(token),
    onSuccess: (response) => {
      if (response.workos_org_id) {
        setAcceptedWorkosOrgId(response.workos_org_id);
      }
      completeAcceptance.mutate(response.workos_org_id);
    },
    onError: (error: Error) => {
      showToast.error("Invitation failed", {
        description: error.message || "Unable to accept invitation.",
      });
    },
  });

  const {
    data: invitation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["invitation-token", token],
    queryFn: () => getInvitationTokenInfo(token),
    enabled: !!token,
    retry: false,
  });

  if (!token) {
    return <InviteError message="No invitation token provided." />;
  }

  if (isLoading) {
    return <InviteSkeleton />;
  }

  if (error || !invitation) {
    return <InviteError message="This invitation is invalid or has expired." />;
  }

  const invitationNotPending =
    invitation.invitation_state && invitation.invitation_state !== "pending";

  if (invitationNotPending) {
    return (
      <InviteError
        message={
          NOT_PENDING_MESSAGES[invitation.invitation_state!] ??
          "This invitation is no longer valid."
        }
      />
    );
  }

  const inviterLabel = invitation.inviter_name || "Your team";
  const signedInWithDifferentEmail = user
    ? invitationEmailsAreDefinitelyDifferent(user.email, invitation.email)
    : false;

  function handleAccept() {
    if (acceptedWorkosOrgId) {
      completeAcceptance.mutate(acceptedWorkosOrgId);
      return;
    }
    acceptExisting.mutate();
  }

  function renderInviteAction() {
    if (!invitation) return null;
    if (user && !signedInWithDifferentEmail) {
      return (
        <ExistingUserAccept
          email={user.email}
          orgName={invitation.org_name}
          pending={acceptExisting.isPending || completeAcceptance.isPending}
          onAccept={handleAccept}
        />
      );
    }

    if (signedInWithDifferentEmail && user) {
      return (
        <WrongAccount
          invitationToken={token}
          invitedEmail={invitation.email}
          signedInEmail={user.email}
        />
      );
    }

    return (
      <UnauthenticatedInviteActions
        token={token}
        orgName={invitation.org_name}
      />
    );
  }

  return (
    <>
      <AuthPageHeader
        title="You've been invited"
        subtitle={
          <>
            {inviterLabel} invited you to join{" "}
            <span className="font-medium text-heading">
              {invitation.org_name}
            </span>{" "}
            as a{" "}
            <span className="font-medium text-heading">{invitation.role}</span>.
          </>
        }
      />

      <InviteOrgCard invitation={invitation} />

      {renderInviteAction()}
    </>
  );
}

export function Invite() {
  return (
    <Suspense fallback={<InviteSkeleton />}>
      <InviteContent />
    </Suspense>
  );
}
